import React,{useState,useEffect,useRef} from 'react'
import axios from '../plugins/axios'
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import Toast from '../plugins/useToast';
import { useRecoilState } from 'recoil';
import { isFromSearchState, selectBedInfoState } from '../plugins/recoil';
import { checkEmptyValue } from '../plugins/common';
import TabularTrend from './TrendReviewV20/TabularTrend';
import Timeline from './TrendReviewV20/Timeline';
import TimelineDateModal from './TrendReviewV20/TimelineDateModal';
import HalfMenus from './halfMenus';
import {ReactComponent as IconErrorYellow} from '../assets/icons/ic-error-yellow.svg';

const TrendReviewV20 = ({isTrendFull, onClose, onAlarmReview, onSetting,onMeasurements, handleFullScreen}) => {
    const { t } = useTranslation();    
    const wrapperRef = useRef(null);    
    const searchModalRef = useRef(null);
    const [selectBedInfo, ] = useRecoilState(selectBedInfoState)            
    const [isFromSearch, ] = useRecoilState(isFromSearchState)
    const [patientHistories, setPatientHistories] = useState([])      
    const [alarmList,setAlarmList] = useState([])    
    const [startDate, setStartDate] = useState(null) //시작 날짜
    const [endDate, setEndDate] = useState(null) //종료 날짜
    const [isTimeSettings, setIsTimeSettings] = useState(false); //TimeLine Setting show / hide     
    const [currentUserInfo, setCurrentUserInfo] = useState({}); // 현재 시간 사용자 정보    
    const [tabularTrendData, setTabularTrendData] = useState([]);
    const [canSearchMoves, setCanSearchMoves] = useState({
        trendPrev : false,
        trendNext : false,
    });    
    
    // deviceHistory에서 입퇴원 기간(periods) 추출
    const getAdmissionPeriods = () => {
        const deviceHistory = selectBedInfo?.deviceHistory || [];
        const sortedHistory = deviceHistory.slice().sort((a, b) => moment(a.createdAt) - moment(b.createdAt));
        const periods = [];
        let currentPeriod = null;
        sortedHistory.forEach(h => {
            if (h.action === 'ADMITTED') {
                currentPeriod = {
                    serial: h.serial,
                    start: moment(h.createdAt),
                    end: null
                };
            } else if (h.action === 'UNASSIGNED' && currentPeriod) {
                currentPeriod.end = moment(h.createdAt);
                periods.push(currentPeriod);
                currentPeriod = null;
            }
        });
        // 아직 퇴원하지 않은 경우 현재 시간까지
        if (currentPeriod && !currentPeriod.end) {
            currentPeriod.end = moment();
            periods.push(currentPeriod);
        }
        return periods;
    };

    // 검색 구간(searchStart~searchEnd)과 겹치는 입퇴원 기간만 추출
    const getOverlappingPlans = (periods, searchStart, searchEnd) => {
        const plans = [];
        periods.forEach(period => {
            const planStart = moment.max(searchStart, period.start);
            const planEnd = moment.min(searchEnd, period.end);
            if (planEnd.isAfter(planStart)) {
                plans.push({
                    serial: period.serial,
                    start: planStart,
                    end: planEnd
                });
            }
        });
        return plans;
    };

    // 전체 입퇴원 기간 대비 현재 24시간 윈도우로 이전/다음 이동 가능 여부 계산
    const updateCanSearchMoves = (periods) => {
        if (!periods || periods.length === 0) {
            setCanSearchMoves({ trendPrev: false, trendNext: false });
            return;
        }
        const earliestStart = moment.min(periods.map(p => p.start));
        const latestEnd = moment.max(periods.map(p => p.end));
        setCanSearchMoves({
            trendPrev: moment(startDate).isAfter(earliestStart),
            trendNext: moment(endDate).isBefore(latestEnd),
        });
    };

    // Tabular Trend 데이터로 TimeLine 및 Histroy 데이터가 필요하여 데이터를 뺴야됨.
    const getTabularTrendData = async () => {
        try{
            const res = await axios.get(`/trend/hl7/disconnect/vitals?serialNo=${selectBedInfo.serial}&startTime=${moment.utc(startDate).format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment.utc(endDate).format('YYYY-MM-DDTHH:mm:ss')}Z&device=V20`)

            const processData = res.data.datas            
            .map(item => ({
                ...item,
                messageTimestamp: moment(item.messageTimestamp).format('YYYY-MM-DD HH:mm:ss'),
                nibp: `${checkEmptyValue(item.nibpSys)}/${checkEmptyValue(item.nibpDia)}/${checkEmptyValue(item.nibpMap)}`,
                ibp: `${checkEmptyValue(item.ibpSys)}/${checkEmptyValue(item.ibpDia)}/${checkEmptyValue(item.ibpMean)}`,
            }));

            setTabularTrendData(processData)
        }
        catch(err){
            console.warn('getTabularTrendData error:', err);
        }
    }


    // Search 기준 데이터를 가져오는 로직
    // 입퇴원이 여러 구간일 경우 각 구간별 serial로 API 호출 후 timestamp 기준 병합
    const getIsSearchTabularTrendData = async () => {
        try {
            const periods = getAdmissionPeriods();
            const searchStart = moment(startDate);
            const searchEnd = moment(endDate);

            // 이전/다음 24시간 이동 가능 여부 업데이트
            updateCanSearchMoves(periods);

            // 검색 구간과 겹치는 입퇴원 기간만 추출
            const plans = getOverlappingPlans(periods, searchStart, searchEnd);

            // 각 구간별로 해당 serial을 사용하여 V20 데이터 가져오기
            let allData = [];
            for (const plan of plans) {
                const res = await axios.get(
                    `/trend/hl7/disconnect/vitals?serialNo=${plan.serial}&startTime=${plan.start.utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${plan.end.utc().format('YYYY-MM-DDTHH:mm:ss')}Z&device=V20`
                );
                if (res.code != '200') throw res;

                const processData = (res.data.datas || []).map(item => ({
                    ...item,
                    messageTimestamp: moment(item.messageTimestamp).format('YYYY-MM-DD HH:mm:ss'),
                    nibp: `${checkEmptyValue(item.nibpSys)}/${checkEmptyValue(item.nibpDia)}/${checkEmptyValue(item.nibpMap)}`,
                    ibp: `${checkEmptyValue(item.ibpSys)}/${checkEmptyValue(item.ibpDia)}/${checkEmptyValue(item.ibpMean)}`,
                }));
                allData = allData.concat(processData);
            }

            // timestamp 기준으로 정렬하여 여러 구간의 데이터를 시간순으로 병합
            allData.sort((a, b) => moment(a.messageTimestamp) - moment(b.messageTimestamp));

            setTabularTrendData(allData);
        } catch (err) {
            console.warn('getIsSearchTabularTrendData error:', err);
            setTabularTrendData([]);
        }
    }
    
    //환자 히스토리.
    //Tarbular Trend 데이터 기준으로 History 데이터를 만들어야 될 듯
    //messageTimestamp 
    const getPatientHistory = async () => {
        try {
            // const res = await axios.get(`/device/${selectBedInfo?.deviceId}/patient/histories?startDate=${moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endDate=${moment(endDate).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`)
            // if (res.code != '200') throw res
            // setPatientHistories(res.data.histories)                 
            
            console.log('tabularTrendData : ', tabularTrendData);

            /* 
                1. 결과 리스트를 가져온다.
                2. devicePatientId 별로 그룹화한다.
                3. 각 그룹에서 messageTimestamp가 가장 최신인 항목을 찾고 그 항목만 따로 남겨 리스트를 만든다.                
                4. 그룹에 selectBedInfo.devicePatientId와 일치하는 항목이 있는지 확인한다.
                4-1. 일치하는 항목이 있으면 Now로 Status를 세팅해준다.
                4-2. 없으면 past로 세팅해준다.
            */ 

        } catch (err) { console.warn(err) }
    }

    const getPatientHistoryV2 = () => {
        if (!tabularTrendData?.length) {
            setPatientHistories([])
            setCurrentUserInfo({
                patientName: selectBedInfo?.patientName || '-',
                devicePatientId: selectBedInfo?.devicePatientId || '-',
                flag: 'now'
            })
            return
        }

        // 1. devicePatientId 별로 그룹화 (빈 값은 '' 키로 묶음)
        const grouped = {}
        tabularTrendData.forEach(item => {
            const pid = item.devicePatientId || ''
            if (!grouped[pid]) grouped[pid] = []
            grouped[pid].push(item)
        })

        // 2. 각 그룹에서 messageTimestamp가 가장 최신인 항목만 추출
        const latestList = Object.entries(grouped).map(([pid, items]) => {
            const latest = items.reduce((prev, curr) => 
                moment(curr.messageTimestamp).isAfter(moment(prev.messageTimestamp)) ? curr : prev
            )
            return { ...latest }
        })

        // 3. selectBedInfo.devicePatientId와 비교하여 status 세팅
        const currentPid = selectBedInfo?.devicePatientId
        const result = latestList.map(item => ({
            ...item,
            patientName: '-',
            devicePatientId: item.devicePatientId,
            flag: item.devicePatientId === currentPid ? 'Now' : 'Past'
        }))

        // 현재 환자 정보 세팅 (Now인 항목 우선, 없으면 가장 과거 항목)
        const nowUser = result.find(item => item.flag === 'Now')
        const oldestUser = result.reduce((oldest, item) => 
            !oldest || moment(item.messageTimestamp).isBefore(moment(oldest.messageTimestamp)) ? item : oldest
        , null)
        const currentUser = nowUser || oldestUser
        if (currentUser) {
            setCurrentUserInfo({
                ...currentUser,
                flag: currentUser.flag === 'Now' ? 'now' : 'past'
            })
        }        
        console.log('currentUser : ', currentUser );
        console.log('result : ', result );

        setPatientHistories(result)
    }

    // Alarm List 가져오기.
    const getAlarmList = async () => {
        try {
            const res = await axios.get(`/trend/alarm/event?serialNo=${selectBedInfo?.serial}&startTime=${moment(startDate).utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${moment(endDate).utc().format('YYYY-MM-DDTHH:mm:ss')}Z`)
            if (res.code != '200') throw res
            const alarmData = res.data.events.map((alarm, idx) => {
                return {
                    ...alarm,
                    alarmNum: idx,
                    startTime: moment(alarm.startTime).format('YYYY-MM-DD HH:mm:ss'),
                    endTime: moment(alarm.endTime).format('YYYY-MM-DD HH:mm:ss'),
                }
            })
            setAlarmList(alarmData);            
        } catch (err) { 
            console.warn(err)           
        }
    }
    
    // Search 일때 AlarmList 가져오기 (공통 헬퍼 사용)
    const getIsSearchAlarmList = async () => {
        try {
            const periods = getAdmissionPeriods();
            const searchStart = moment(startDate);
            const searchEnd = moment(endDate);
            const plans = getOverlappingPlans(periods, searchStart, searchEnd);

            // 각 구간별 serial로 알람 데이터 가져오기
            let allEvents = [];
            for (const plan of plans) {
                const res = await axios.get(
                    `/trend/alarm/event?serialNo=${plan.serial}&startTime=${plan.start.utc().format('YYYY-MM-DDTHH:mm:ss')}Z&endTime=${plan.end.utc().format('YYYY-MM-DDTHH:mm:ss')}Z`
                );
                if (res.code != '200') throw res;
                const events = (res.data.events || []).map((alarm, idx) => ({
                    ...alarm,
                    alarmNum: idx,
                    startTime: moment(alarm.startTime).format('YYYY-MM-DD HH:mm:ss'),
                    endTime: moment(alarm.endTime).format('YYYY-MM-DD HH:mm:ss'),
                }));
                allEvents = allEvents.concat(events);
            }

            // 시간순 정렬 및 중복 제거
            const sortedEvents = allEvents
                .sort((a, b) => moment(a.startTime) - moment(b.startTime))
                .filter((item, idx, arr) => idx === 0 || item.startTime !== arr[idx - 1].startTime);

            setAlarmList(sortedEvents);
        } catch (err) {
            console.warn(err);
            setAlarmList([]);
        }
    };

    // TimeLine 버튼 클릭 시 변경. v20 기준으로는 24시간씩 이동하게 수정
    // Search 모드에서는 canSearchMoves로 이동 가능 여부 체크, 입퇴원 경계 clamp
    const handleChangeTime = (flag) => {        
        switch(flag) {
            case 'prevDuration':
                    if (isFromSearch && !canSearchMoves.trendPrev) return;
                    
                    if (isFromSearch) {
                        const periods = getAdmissionPeriods()
                        const earliestStart = moment.min(periods.map(p => p.start))
                        const latestEnd = moment.max(periods.map(p => p.end))
                        // startDate 기준으로 24시간 이전으로 이동, 항상 24시간 윈도우 유지
                        let newStart = moment(startDate).subtract(24, 'hours')
                        if (newStart.isBefore(earliestStart)) {
                            newStart = earliestStart.clone()
                        }
                        let newEnd = newStart.clone().add(24, 'hours')
                        if (newEnd.isAfter(latestEnd)) {
                            newEnd = latestEnd.clone()
                        }
                        setStartDate(new Date(newStart))
                        setEndDate(new Date(newEnd))
                    } else {
                        setStartDate(new Date(moment(startDate).subtract(24, 'hours')))
                        setEndDate(new Date(moment(endDate).subtract(24, 'hours')))
                    }
                break;
            case 'nextDuration':
                    if (isFromSearch && !canSearchMoves.trendNext) return;
                    
                    if (isFromSearch) {
                        const periods = getAdmissionPeriods()
                        const earliestStart = moment.min(periods.map(p => p.start))
                        const latestEnd = moment.max(periods.map(p => p.end))
                        // endDate 기준으로 24시간 이후로 이동, 항상 24시간 윈도우 유지
                        let newEnd = moment(endDate).add(24, 'hours')
                        if (newEnd.isAfter(latestEnd)) {
                            newEnd = latestEnd.clone()
                        }
                        let newStart = newEnd.clone().subtract(24, 'hours')
                        if (newStart.isBefore(earliestStart)) {
                            newStart = earliestStart.clone()
                        }
                        setStartDate(new Date(newStart))
                        setEndDate(new Date(newEnd))
                    } else {
                        setStartDate(new Date(moment(startDate).add(24, 'hours')))
                        setEndDate(new Date(moment(endDate).add(24, 'hours')))
                    }                                            
                break;
            default :
                break;
        }
    }    
    
    // 타임라인 Settings show / hide
    const handleTimeSettings = () => {
        setIsTimeSettings(true);
    }

    // 타임라인 Settings 저장
    const handleTimeLineSearch = (startDate, endDate, duration) => {
        setStartDate(new Date(moment(startDate)));
        setEndDate(new Date(moment(endDate)));        
        setIsTimeSettings(false);
    }

    // V20 Print Handler 
    const handleV20TrendPrint = async () => {
        const waveStratTime = moment.utc(startDate).format('YYYY-MM-DDTHH:mm:ss') + 'Z';
        const waveEndTime = moment.utc(endDate).format('YYYY-MM-DDTHH:mm:ss') + 'Z';

        let setUrl = '';
        const windowFeatures = "width=1200,height=800,left=100,top=100,scrollbars=yes,resizable=yes";        
        setUrl = `${window.location.origin}/print/v20trendform?deviceid=${selectBedInfo.deviceId}&starttime=${waveStratTime}&endtime=${waveEndTime}`

        const newWindow = window.open(setUrl, "popupWindow", windowFeatures);
        if (newWindow) {
            newWindow.focus();
        } else {
            Toast({ isSuccess: false, msg: t(`error.failOpenPage`) });
        }
    }

    // 타임라인 설정 화면 버튼 클릭 시 
    useEffect(() => {
        if(isTimeSettings){            
            searchModalRef.current?.setDate(startDate,endDate);
        }
    }, [isTimeSettings])   

    //TimeLine이 세팅되면 환자 이력, Alarm List 가져오기
    useEffect(() => {                
        if(moment(endDate).isValid()){
            if(isFromSearch){
                getIsSearchTabularTrendData();          
            }else{                
                getTabularTrendData();    
            }
        }
    }, [endDate])
    
    //데이터가 세팅되면 AlarmList 및 History List만들기
    useEffect(() => {
        if(tabularTrendData && tabularTrendData.length > 0){
            if(isFromSearch){
                getIsSearchAlarmList();
            }else{                                
                getPatientHistoryV2();
                getAlarmList();
            }
        } else {
            getPatientHistoryV2();
        }
    }, [tabularTrendData])
    
    useEffect(() => {
        if (selectBedInfo) {     
            if(isFromSearch){
                //입원 startTime이 24시간보다 짧은 경우에는 startTime ~ endTime으로 세팅, 그 이상인 경우에는 endTime 기준으로 24시간 세팅
                if(moment(selectBedInfo.endTime).diff(moment(selectBedInfo.startTime), 'hours') < 24){
                    setStartDate(new Date(moment(selectBedInfo.startTime)))
                }else{
                    setStartDate(new Date(moment(selectBedInfo.endTime).subtract(24, 'hours')))
                }                
                setEndDate(new Date(moment(selectBedInfo.endTime)))                
            }else{
                setStartDate(new Date(moment().subtract(24, 'hours')))
                setEndDate(new Date(moment()))                
            }            
        }
    }, [selectBedInfo]);

    return (
        <div className="relative w-full h-full bg-black" style={{  }}>
            <div ref={wrapperRef} className="flex flex-col w-full h-full">
                <div className="flex relative justify-center text-white items-center h-[32px]" style={{ color: isFromSearch ? 'white' : '', background: isFromSearch ? '#01004B' : (currentUserInfo?.flag == 'past' ? '#02008B' : '#2C2C2C'), borderBottomWidth: 1, borderColor: '#4E4E4E' }}>
                    <div className={`flex items-center px-2 py-1`} style={{ background: isFromSearch ? 'linear-gradient(180deg, #2D2C81 0%, #141352 100%)' : (currentUserInfo?.flag == 'past' ? 'linear-gradient(180deg, #2927AC 0%, #100E95 100%)' : 'linear-gradient(180deg, #DEDEDE 0%, #9F9F9F 100%)'), height: '100%' }}>
                        <div className="flex items-center justify-center mr-2 w-[178px]" style={{ color: (currentUserInfo?.flag == 'past' || isFromSearch) ? 'white' : 'black', fontWeight: 500, fontSize: 24, lineHeight: '26.4px' }}>{isFromSearch ? '-' : checkEmptyValue(selectBedInfo.bedNumber.length <= 1 ? `0${selectBedInfo.bedNumber}` : selectBedInfo.bedNumber, 'BED-')}</div>                        
                        <div className="truncate" style={{ color: (currentUserInfo?.flag == 'past' || isFromSearch) ? 'white' : 'black', fontWeight: 200, fontSize: 16, lineHeight: '17.6px', minWidth: 301, maxWidth: 301 }}>                            
                            {
                                isFromSearch ? (
                                    <>
                                        {`${checkEmptyValue(selectBedInfo.patientName)} , ${checkEmptyValue(selectBedInfo.devicePatientId)}`}
                                    </>
                                ) : (
                                    <>
                                        {
                                            currentUserInfo?.flag == 'now' ? (
                                                <>
                                                    { selectBedInfo.patientId == null ? t('trend.notAdmitted') : selectBedInfo.nameDisplay === '1' ? checkEmptyValue(selectBedInfo.patientName) : selectBedInfo.nameDisplay === '2' ? checkEmptyValue(selectBedInfo.devicePatientId) : selectBedInfo.nameDisplay === '3' ? `${checkEmptyValue(selectBedInfo.patientName)} , ${checkEmptyValue(selectBedInfo.devicePatientId)}`: t('trend.notAdmitted')}
                                                </>
                                            ) : (
                                                <>
                                                    { `${currentUserInfo.patientName == t('trend.notAdmitted') ? currentUserInfo.patientName : `${checkEmptyValue(currentUserInfo.patientName)} , ${checkEmptyValue(currentUserInfo.devicePatientId)}`}` }
                                                </>
                                            )
                                        }
                                    </>
                                )
                            }
                        </div>
                    </div>
                    {
                        isFromSearch &&
                        <div className="ml-3">
                            { `${moment(selectBedInfo.createdAt).format('YYYY-MM-DD HH:mm:ss')} ~ ${selectBedInfo.status !== 'ADMITTED' ? moment(selectBedInfo.updatedAt).format('YYYY-MM-DD HH:mm:ss') : moment(selectBedInfo.endTime).format('YYYY-MM-DD HH:mm:ss')}` }
                        </div>
                    }
                    {
                        !isFromSearch &&
                        currentUserInfo?.flag == 'past' &&
                        <div className="flex items-center text-[#FFF500] ml-3">
                            <IconErrorYellow />
                            <div className="ml-1" style={{ fontWeight: 200, fontSize: 16 }}>{t('trend.notCurrentPatient')}</div>
                        </div>
                    }                    
                    <HalfMenus
                        selectBedInfo={selectBedInfo}
                        nowPage={'trend'}
                        onTrend={null}
                        onAlarmReview={onAlarmReview}
                        onMeasurements={onMeasurements}
                        onSetting={onSetting}
                        isFull={isTrendFull}
                        isFromSearch={isFromSearch}
                        handleFullScreen={handleFullScreen}
                        onClose={onClose}
                    />
                </div>

                <div className="flex flex-grow text-white" style={{ height: isTrendFull ? '100%' : 'calc(100% - 32px)' }}>
                    <div className="relative flex flex-col flex-grow w-full h-full">                                               
                        <div className='flex' style={{ height: 'calc(100% - 65px)' }}>
                            <TabularTrend
                                tabularTrendData={tabularTrendData}                                        
                                handleTrendPrint={() => handleV20TrendPrint()}          
                                alarmList={alarmList}                                                                                    
                            />
                        </div>
                        <div className='relative h-[65px] bg-[#000000]'>
                            <Timeline                                 
                                patientHistories={patientHistories}                                
                                alarmList={alarmList} 
                                startTime={moment.utc(startDate)} 
                                endTime={moment.utc(endDate)}                                
                                handleChangeTime={(...args) => { handleChangeTime(...args) }}                                                                
                                handleTimeSettings={handleTimeSettings}                                                               
                                canSearchMoves={canSearchMoves}                                                             
                            />
                        </div>
                    </div>
                </div>
            </div>

            {
                isTimeSettings && 
                <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 flex items-center justify-center z-[999]">
                    <TimelineDateModal 
                        ref={searchModalRef}
                        handleClose={() => setIsTimeSettings(false)}
                        handleSearch={handleTimeLineSearch}                                            
                    />    
                </div>
            }
        </div>
    )
}

export default React.memo(TrendReviewV20)